import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, IconButton, InputAdornment,
  LinearProgress, List, ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, Paper, Snackbar, Stack,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import ArchiveIcon from '@mui/icons-material/Archive';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import apiService from '../services/api.service';
import SoaPreviewFrame from '../components/formattedSoa/PreviewFrame';
import { printPdfBlob, saveBlob, soaBlobErrorMessage, soaErrorMessage } from '../utils/formattedSoa';
import { SoaAgingBasis, SoaCardType, SoaConfig, SoaPartner, SoaRun, SoaTemplateSummary } from '../types/formattedSoa';

/**
 * Sales Portal > Formatted SOA: statements of account laid out by the company's templates (designed in
 * Statement Templates), for one or many customers or suppliers over a period. Up to 25 statements come
 * back as a PDF at once; any selection (up to 3,000) can run in the background into one PDF and a ZIP of
 * one PDF per business partner. docs/FORMATTED_SOA.md.
 */

const DIRECT_LIMIT = 25;
const RUN_LIMIT = 3000;

const amount = (n: number | null | undefined): string =>
  n === null || n === undefined ? '' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 });

const runStatusColor: Record<SoaRun['status'], 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  Queued: 'default', Running: 'info', Done: 'success', Failed: 'error', Cancelled: 'warning',
};

const FormattedSOA: React.FC = () => {
  const navigate = useNavigate();
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || 0, []);
  const [config, setConfig] = useState<SoaConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cardType, setCardType] = useState<SoaCardType>('C');
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // As Customer SOA: the ageing measured from the invoice (posting) date or the due date, at the To date.
  const [basis, setBasis] = useState<SoaAgingBasis>('invoice');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<SoaPartner[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<SoaPartner[]>([]);
  const [previewCode, setPreviewCode] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'pdf' | 'print' | 'run' | 'balance' | null>(null);
  const [zip, setZip] = useState(true);
  const [runs, setRuns] = useState<SoaRun[]>([]);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'warning' | 'info' } | null>(null);
  const searchSeq = useRef(0);

  const notify = useCallback((msg: string, sev: 'success' | 'error' | 'warning' | 'info' = 'error') => setToast({ msg, sev }), []);

  useEffect(() => {
    if (!companyId) return;
    apiService.getSoaConfig(companyId)
      .then((res: any) => {
        const cfg: SoaConfig = res.data;
        setConfig(cfg);
        setFromDate(cfg.defaults.fromDate);
        setToDate(cfg.defaults.toDate);
      })
      .catch((e: any) => setLoadError(soaErrorMessage(e, 'Formatted SOA could not be loaded.')));
  }, [companyId]);

  const templates: SoaTemplateSummary[] = useMemo(
    () => (config?.templates ?? []).filter((t) => t.appliesTo === 'ALL' || t.appliesTo === cardType),
    [config, cardType]
  );

  useEffect(() => {
    if (!templates.length) { setTemplateId(''); return; }
    setTemplateId((cur) => (templates.some((t) => t.templateId === cur) ? cur : (templates.find((t) => t.isDefault) ?? templates[0]).templateId));
  }, [templates]);

  // Partner search: debounced; the latest answer wins.
  useEffect(() => {
    if (!config) return undefined;
    const seq = ++searchSeq.current;
    const handle = setTimeout(() => {
      setSearching(true);
      apiService.getSoaPartners(companyId, cardType, search.trim())
        .then((res: any) => { if (seq === searchSeq.current) setResults(res.data ?? []); })
        .catch((e: any) => { if (seq === searchSeq.current) { setResults([]); notify(soaErrorMessage(e, 'The search failed.')); } })
        .finally(() => { if (seq === searchSeq.current) setSearching(false); });
    }, 350);
    return () => clearTimeout(handle);
  }, [search, cardType, config, companyId, notify]);

  const loadRuns = useCallback(() => {
    if (!companyId || !config) return;
    apiService.getSoaRuns(companyId).then((res: any) => setRuns(res.data ?? [])).catch(() => undefined);
  }, [companyId, config]);

  useEffect(() => { loadRuns(); }, [loadRuns]);
  const active = runs.some((r) => r.status === 'Queued' || r.status === 'Running');
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(loadRuns, 3000);
    return () => clearInterval(t);
  }, [active, loadRuns]);

  const switchType = (t: SoaCardType | null) => {
    if (!t || t === cardType) return;
    setCardType(t);
    setSelected([]);
    setResults([]);
    setPreviewHtml(null);
    setPreviewCode(null);
  };

  const selectedCodes = useMemo(() => new Set(selected.map((p) => p.cardCode.toUpperCase())), [selected]);
  const toggle = (p: SoaPartner) => setSelected((cur) => (selectedCodes.has(p.cardCode.toUpperCase())
    ? cur.filter((x) => x.cardCode.toUpperCase() !== p.cardCode.toUpperCase())
    : [...cur, p]));
  const addAll = (list: SoaPartner[]) => setSelected((cur) => {
    const have = new Set(cur.map((p) => p.cardCode.toUpperCase()));
    const merged = [...cur, ...list.filter((p) => !have.has(p.cardCode.toUpperCase()))];
    if (merged.length > RUN_LIMIT) notify(`A run takes at most ${RUN_LIMIT.toLocaleString()} business partners; the first ${RUN_LIMIT.toLocaleString()} are selected.`, 'warning');
    return merged.slice(0, RUN_LIMIT);
  });

  const allWithBalance = async () => {
    setBusy('balance');
    try {
      const res: any = await apiService.getSoaPartners(companyId, cardType, '', true);
      const list: SoaPartner[] = res.data ?? [];
      addAll(list);
      notify(`${list.length.toLocaleString()} ${cardType === 'S' ? 'suppliers' : 'customers'} with a balance in SAP.`, 'info');
    } catch (e: any) {
      notify(soaErrorMessage(e, 'The list could not be read from SAP.'));
    } finally {
      setBusy(null);
    }
  };

  const periodOk = !!fromDate && !!toDate && fromDate <= toDate;
  const body = (codes: string[]) => ({ companyId, templateId: templateId || undefined, cardType, cardCodes: codes, fromDate, toDate, basis });

  const preview = async (code: string, refresh = false) => {
    if (!periodOk) { notify('The From date must be on or before the To date.'); return; }
    setPreviewCode(code);
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res: any = await apiService.previewSoa({ ...body([code]), refresh });
      setPreviewHtml(res.data.html);
    } catch (e: any) {
      setPreviewHtml(null);
      setPreviewError(soaErrorMessage(e, 'The statement could not be prepared.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const direct = async (mode: 'pdf' | 'print', codes: string[]) => {
    if (!periodOk) { notify('The From date must be on or before the To date.'); return; }
    setBusy(mode);
    try {
      const { blob, fileName, notes } = await apiService.downloadSoaPdf(body(codes));
      if (mode === 'pdf') saveBlob(blob, fileName); else printPdfBlob(blob);
      if (notes.length) notify(`Printed with notes: ${notes.join(' ')}`, 'warning');
    } catch (e: any) {
      notify(await soaBlobErrorMessage(e, 'The PDF could not be prepared.'));
    } finally {
      setBusy(null);
    }
  };

  const startRun = async () => {
    if (!periodOk) { notify('The From date must be on or before the To date.'); return; }
    setBusy('run');
    try {
      await apiService.startSoaRun({ ...body(selected.map((p) => p.cardCode)), zip });
      notify(`Preparing ${selected.length.toLocaleString()} statements in the background; the files appear under Runs.`, 'success');
      loadRuns();
    } catch (e: any) {
      notify(soaErrorMessage(e, 'The run could not be started.'));
    } finally {
      setBusy(null);
    }
  };

  const downloadRun = async (run: SoaRun, kind: 'pdf' | 'zip') => {
    try {
      const { blob, fileName } = await apiService.downloadSoaRun(run.runId, kind);
      saveBlob(blob, fileName);
    } catch (e: any) {
      notify(await soaBlobErrorMessage(e, 'The file could not be downloaded.'));
    }
  };

  const cancelRun = async (run: SoaRun) => {
    try {
      await apiService.cancelSoaRun(run.runId);
      loadRuns();
    } catch (e: any) {
      notify(soaErrorMessage(e, 'The run could not be cancelled.'));
    }
  };

  if (!companyId) {
    return <Box sx={{ p: { xs: 2, md: 3 } }}><Alert severity="info">Select a company (top bar) to print statements.</Alert></Box>;
  }
  if (loadError) {
    return <Box sx={{ p: { xs: 2, md: 3 } }}><Alert severity="error">{loadError}</Alert></Box>;
  }
  if (!config) {
    return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress aria-label="Loading" /></Box>;
  }

  const n = selected.length;
  const label = cardType === 'S' ? 'suppliers' : 'customers';
  const canRun = n > 0 && periodOk && !!templateId;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" component="h1">Formatted SOA</Typography>
          <Typography variant="body2" color="text.secondary">
            Statements of account from SAP journal lines, laid out by {config.companyName}&apos;s templates.
          </Typography>
        </Box>
        {config.permissions.design && (
          <Button variant="outlined" startIcon={<DesignServicesIcon />} onClick={() => navigate('/sales/formatted-soa/templates')}>
            Statement templates
          </Button>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} useFlexGap flexWrap="wrap">
          <ToggleButtonGroup exclusive size="small" value={cardType} onChange={(_, v) => switchType(v)} aria-label="Statement for">
            <ToggleButton value="C">Customers</ToggleButton>
            <ToggleButton value="S" disabled={!config.permissions.suppliers}>Suppliers</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            select size="small" label="Template" value={templateId} sx={{ minWidth: 220 }}
            onChange={(e) => setTemplateId(Number(e.target.value))}
            helperText={templates.length ? undefined : 'No template for these statements yet'}
          >
            {templates.map((t) => (
              <MenuItem key={t.templateId} value={t.templateId}>{t.name}{t.isDefault ? ' (default)' : ''}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" type="date" label="From date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ max: toDate || undefined }} sx={{ minWidth: 160 }}
          />
          <TextField
            size="small" type="date" label="To date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ min: fromDate || undefined }} sx={{ minWidth: 160 }}
            error={!periodOk && !!fromDate && !!toDate}
          />
          <ToggleButtonGroup
            size="small" exclusive value={basis} aria-label="Age the statement by"
            onChange={(_, v: SoaAgingBasis | null) => {
              if (!v || v === basis) return;
              setBasis(v);
              if (previewCode) setPreviewHtml(null);
            }}
          >
            <ToggleButton value="invoice" aria-label="Age by invoice date">Invoice date</ToggleButton>
            <ToggleButton value="due" aria-label="Age by due date">Due date</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {!config.permissions.suppliers && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Supplier statements need the &quot;Formatted SOA: suppliers&quot; permission.
          </Typography>
        )}
        {cardType === 'C' && !config.permissions.allCustomers && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            You see the customers assigned to you (as in Customer SOA).
          </Typography>
        )}
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(320px, 420px) 1fr' }, gap: 2, alignItems: 'start' }}>
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, minWidth: 0 }}>
          <Typography variant="subtitle1" component="h2" gutterBottom>Business partners</Typography>
          <TextField
            size="small" fullWidth placeholder={`Search ${label} by code or name`} value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ 'aria-label': `Search ${label}` }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <Stack direction="row" spacing={1} sx={{ my: 1 }} useFlexGap flexWrap="wrap">
            <Button size="small" onClick={() => addAll(results)} disabled={!results.length}>Select all results</Button>
            <Button size="small" onClick={() => void allWithBalance()} disabled={busy !== null}>
              {busy === 'balance' ? 'Reading SAP…' : 'All with a balance'}
            </Button>
            <Button size="small" color="inherit" onClick={() => setSelected([])} disabled={!n}>Clear</Button>
          </Stack>
          {searching && <LinearProgress sx={{ mb: 1 }} aria-label="Searching" />}
          <List dense sx={{ maxHeight: 360, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {results.length === 0 && !searching && (
              <ListItem><ListItemText secondary={search ? 'No match.' : `Type to search ${label}.`} /></ListItem>
            )}
            {results.map((p) => (
              <ListItem
                key={p.cardCode}
                disablePadding
                secondaryAction={(
                  <Tooltip title="Preview">
                    <IconButton edge="end" size="small" aria-label={`Preview ${p.cardName}`} onClick={() => void preview(p.cardCode)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              >
                <ListItemButton dense onClick={() => toggle(p)}>
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    <Checkbox edge="start" size="small" checked={selectedCodes.has(p.cardCode.toUpperCase())} tabIndex={-1} disableRipple
                      inputProps={{ 'aria-label': `Select ${p.cardName}` }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${p.cardName}`}
                    secondary={[p.cardCode, p.foreignName, p.balance !== null && p.balance !== undefined ? `Balance ${amount(p.balance)}` : '']
                      .filter(Boolean).join(' · ')}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>
            {n ? `${n.toLocaleString()} selected` : `No ${label} selected`}
          </Typography>
          {n > 0 && (
            <Box sx={{ maxHeight: 150, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {selected.slice(0, 200).map((p) => (
                <Chip key={p.cardCode} size="small" label={p.cardCode} title={p.cardName} onDelete={() => toggle(p)}
                  onClick={() => void preview(p.cardCode)} />
              ))}
              {n > 200 && <Chip size="small" variant="outlined" label={`+${(n - 200).toLocaleString()} more`} />}
            </Box>
          )}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button variant="contained" startIcon={<PictureAsPdfIcon />} disabled={!canRun || n > DIRECT_LIMIT || busy !== null}
              onClick={() => void direct('pdf', selected.map((p) => p.cardCode))}>
              {busy === 'pdf' ? 'Preparing…' : 'PDF'}
            </Button>
            <Button variant="outlined" startIcon={<PrintIcon />} disabled={!canRun || n > DIRECT_LIMIT || busy !== null}
              onClick={() => void direct('print', selected.map((p) => p.cardCode))}>
              {busy === 'print' ? 'Preparing…' : 'Print'}
            </Button>
            <Button variant="outlined" startIcon={<ArchiveIcon />} disabled={!canRun || busy !== null} onClick={() => void startRun()}>
              {busy === 'run' ? 'Starting…' : 'Bulk run'}
            </Button>
          </Stack>
          <FormControlLabel
            sx={{ mt: 0.5 }}
            control={<Checkbox size="small" checked={zip} onChange={(e) => setZip(e.target.checked)} />}
            label={<Typography variant="body2">Bulk run: also a ZIP with one PDF per business partner</Typography>}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            PDF and Print take up to {DIRECT_LIMIT} at once; a bulk run takes up to {RUN_LIMIT.toLocaleString()} and prepares one PDF
            (each statement on new pages, with its own page numbers) in the background.
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} spacing={1}>
            <Typography variant="subtitle1" component="h2" noWrap>
              Preview{previewCode ? `: ${previewCode}` : ''}
            </Typography>
            {previewCode && (
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Read SAP again">
                  <span>
                    <IconButton size="small" aria-label="Read SAP again" onClick={() => void preview(previewCode, true)} disabled={previewLoading}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button size="small" startIcon={<PictureAsPdfIcon />} disabled={busy !== null} onClick={() => void direct('pdf', [previewCode])}>PDF</Button>
                <Button size="small" startIcon={<PrintIcon />} disabled={busy !== null} onClick={() => void direct('print', [previewCode])}>Print</Button>
                <IconButton size="small" aria-label="Close the preview" onClick={() => { setPreviewCode(null); setPreviewHtml(null); setPreviewError(null); }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </Stack>
          <SoaPreviewFrame
            html={previewHtml}
            loading={previewLoading}
            error={previewError}
            height="calc(100vh - 330px)"
            emptyText="Press the eye beside a business partner (or a selected chip) to preview its statement."
          />
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mt: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" component="h2">Runs</Typography>
          <IconButton size="small" aria-label="Refresh the runs" onClick={loadRuns}><RefreshIcon fontSize="small" /></IconButton>
        </Stack>
        {runs.length === 0 && <Typography variant="body2" color="text.secondary">No bulk runs in the last week.</Typography>}
        <Stack spacing={1} sx={{ mt: 1 }}>
          {runs.map((r) => (
            <Paper key={r.runId} variant="outlined" sx={{ p: 1.25 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    <Chip size="small" color={runStatusColor[r.status]} label={r.status} />
                    <Typography variant="body2" fontWeight={600}>
                      {r.total.toLocaleString()} {r.cardType === 'S' ? 'suppliers' : 'customers'} · {r.fromDate} to {r.toDate} · aged by {r.basis === 'due' ? 'due date' : 'invoice date'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.templateName} · {r.createdByName ?? ''} · {new Date(r.createdDate).toLocaleString()}
                    </Typography>
                  </Stack>
                  {r.message && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{r.message}</Typography>}
                  {r.errors.length > 0 && (
                    <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                      {r.errors.slice(0, 3).map((e) => `${e.cardCode}: ${e.message}`).join(' · ')}{r.errors.length > 3 ? ` · +${r.errors.length - 3} more` : ''}
                    </Typography>
                  )}
                  {(r.status === 'Running' || r.status === 'Queued') && (
                    <LinearProgress variant={r.total ? 'determinate' : 'indeterminate'} value={r.total ? (r.done / r.total) * 100 : 0}
                      sx={{ mt: 0.5, maxWidth: 360 }} aria-label="Run progress" />
                  )}
                </Box>
                <Stack direction="row" spacing={1} flexShrink={0}>
                  {r.hasPdf && <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => void downloadRun(r, 'pdf')}>PDF{r.pages ? ` (${r.pages} p.)` : ''}</Button>}
                  {r.hasZip && <Button size="small" startIcon={<ArchiveIcon />} onClick={() => void downloadRun(r, 'zip')}>ZIP</Button>}
                  {(r.status === 'Running' || r.status === 'Queued') && (
                    <Button size="small" color="warning" onClick={() => void cancelRun(r)}>Cancel</Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Paper>

      <Snackbar open={!!toast} autoHideDuration={7000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.sev ?? 'info'} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast?.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FormattedSOA;
